import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Button, TableCell, TableRow } from "@material-ui/core";
import clsx from "clsx";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { getOriginLocationString, getDestinationLocationString } from "../../common/helpers";
import { NeedIcon, NeedListItem, AcuityNeedItem, PriorityBadge } from "./TransferIcons";
import { useHistory } from "react-router";
import { useDrag } from "react-dnd";
import DraggingTransfer from "./planner/DraggingTransfer";
import { getEmptyImage } from "react-dnd-html5-backend";
import { Targets } from "../../common/dndHelper";
import { IVirtualTrip } from "../../common/plannerTypes";
import { IDropResult, TimelineCommandType } from "./types/plannerTypes";
import { getShortFullName } from "./planner/plannerHelpers";
import { getLastVersionVisualState, getLastVersionGlobalState, mapFromIScheduleToSingleVirtualTrip } from "./planner/virtualTripHelpers";
import { useDispatch } from "react-redux";
import { showTripDetailsModal } from "../../store/modules/plannerSliceModals";
import { MODAL_TYPE } from "./planner/PlannerTripDetails";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  tableBody: {
    backgroundColor: "#002047",
    cursor: "pointer",
    "& > *": {
      fontSize: 18,
      fontWeight: 500,
    },
    height: 33,
  },
  tableCell: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    height: 33,
    paddingLeft: 0,
  },
  tableCellStatus: {
    whiteSpace: "normal",
    lineHeight: "22px",
    paddingTop: 0,
    paddingBottom: 0,
    height: 33,
    paddingLeft: 0,
  },
  tableCellStatusWithAction: {
    whiteSpace: "normal",
    lineHeight: "22px",
    paddingTop: 0,
    paddingBottom: 0,
    height: 33,
    paddingLeft: 0,
    display: "flex",
  },
  animatedUpdatedItem: {
    animation: `$updatedTransferEffect 60000ms ${theme.transitions.easing.easeInOut}`,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08) !important",
    },
  },
  "@keyframes updatedTransferEffect": {
    "0%": { backgroundColor: "#F4B8273A" },
    "100%": { backgroundColor: "#002047" },
  },
  needIconCenter: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    minHeight: 33,
    border: 0,
    paddingLeft: 0,
  },
  yellowBoxText: {
    background: "#F4B827",
    padding: "6px 16px",
    fontSize: 14,
    fontWeight: 700,
    color: "#03090E",
    borderRadius: 4,
    marginLeft: 5,
    cursor: "pointer",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  isDragging: {
    opacity: 0.2,
  },
  requestText: {
    marginRight: 10,
    paddingTop: 5,
    float: "left",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
}));

export const timeToAgoString = (date: any): string => {
  const timenow = moment();
  const diffMin = Math.floor(moment.duration(timenow.diff(moment(date))).asMinutes());
  if (diffMin <= 0) {
    return "Just Now";
  } else {
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays > 0 ? diffDays + "d " : ""}${diffHours > 0 ? (diffHours % 20) + "h " : ""}${diffMin % 60 > 0 ? (diffMin % 60) + "m" : ""} ago`;
  }
};

const TransferRequestItem = (props: any) => {
  const {
    transferRequest,
    updatedTransfers,
    isFullTableWidth,
    getRequestedStatusString,
    scheduleRequestedList,
    setCurrentTransferId,
    setCurrentTransferData,
    setCurrentBookingId,
    setShowScheduleModal,
    showAdHocRequestModal,
  } = props;

  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: "TransferRequest",
    item: {
      transferId: transferRequest.transferId,
      priority: transferRequest?.priority,
      pickupBeforeTime: transferRequest?.pickupBeforeTime,
      pickupAfterTime: transferRequest?.pickupAfterTime,
      dropoffBeforeTime: transferRequest?.dropoffBeforeTime,
      dropoffAfterTime: transferRequest?.dropoffAfterTime,
      transferAcuity: transferRequest?.classification,
    },
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult() as IDropResult;
      dispatch(
        showTripDetailsModal({
          showTripDetails: false,
          tripDetails: null,
          tripDetailsModalType: MODAL_TYPE.TRIP_DETAILS,
          blockLabel: null,
        })
      );
      if (item && dropResult && !dropResult.target) {
        const lastVersionOfGlobalState = getLastVersionGlobalState().map((r: IVirtualTrip) => r.transfers[0].scheduleObj);
        const lastVersionOfTransfer = lastVersionOfGlobalState.find((t) => t.transferId === transferRequest.transferId);
        showAdHocRequestModal({
          transferId: transferRequest.transferId,
          mobility: lastVersionOfTransfer?.mobility,
          priority: lastVersionOfTransfer?.priority,
          pickupBeforeTime: lastVersionOfTransfer?.pickupBeforeTime,
          pickupAfterTime: lastVersionOfTransfer?.pickupAfterTime,
          dropoffBeforeTime: lastVersionOfTransfer?.dropoffBeforeTime,
          dropoffAfterTime: lastVersionOfTransfer?.dropoffAfterTime,
        });
        return;
      }
      if (dropResult && dropResult.target === Targets.PLANNER_TIMELINE) {
        if (dropResult?.plannerMinute?.trip) {
          // mostly insurance that it take last version of the trip
          const lastVersionOfTrip = getLastVersionVisualState().find((v) => v.virtualTripId === dropResult.plannerMinute?.trip?.virtualTripId);
          var updatedLastVersionOfTrip:any={...lastVersionOfTrip};
          if (lastVersionOfTrip && updatedLastVersionOfTrip) {
            updatedLastVersionOfTrip.isTripMinOverrided = false;
            updatedLastVersionOfTrip.overrideTripEstimateInMin = 0;
          }

          dropResult.applyTimelineChanges(mapFromIScheduleToSingleVirtualTrip(transferRequest), {
            type: TimelineCommandType.addToTrip,
            tripUpdates: updatedLastVersionOfTrip,
          });
          return;
        } else {
          dropResult.applyTimelineChanges(mapFromIScheduleToSingleVirtualTrip(transferRequest), {
            type: TimelineCommandType.moveTrip,
            plannerMinute: dropResult.plannerMinute,
          });
          return;
        }
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      handlerId: monitor.getHandlerId(),
    }),
  }));

  React.useEffect(() => {
    preview(getEmptyImage());
  }, []);  

  return (
    <>
      <TableRow
        onClick={(e) => history.push(`/transferDetails/${transferRequest.bookingId}/${transferRequest.transferId}`)}
        hover
        key={
          transferRequest.transferId +
          (updatedTransfers.find((e: any) => e.TransferId === transferRequest.transferId)
            ? updatedTransfers.find((e: any) => e.TransferId === transferRequest.transferId).UpdatedDate.getTime()
            : "")
        }
        className={clsx(classes.tableBody, {
          [classes.animatedUpdatedItem]: updatedTransfers.some(
            (e: any) => e.TransferId === transferRequest.transferId && new Date().getTime() - e.UpdatedDate < 60000
          ),
          [classes.isDragging]: isDragging,
        })}
        ref={drag}
      >
        <TableCell className={classes.tableCell}>{timeToAgoString(transferRequest.createdOn)}</TableCell>
        {isFullTableWidth && (
          <TableCell className={classes.tableCell}>
            {transferRequest.firstName[0]} {transferRequest.lastName}
          </TableCell>
        )}
        {isFullTableWidth && (
          <TableCell className={classes.needIconCenter}>
            {transferRequest.bookingSpecialNeeds.map((item: any) => (
              <NeedIcon
                key={transferRequest.bookingId + item.specialNeedId}
                iconName={item.specialNeedIconUrl.split("/")[item.specialNeedIconUrl.split("/").length - 1]}
              />
            ))}
            {transferRequest.mobility === 1 && <NeedListItem key="notambulant" text={"Not Ambulant"} iconUrl={"../../assets/svgs/nonambulant.svg"} />}
            {transferRequest.mobility !== 1 && <NeedListItem key="ambulant" text={"Ambulant"} iconUrl={"../../assets/svgs/ambulant.svg"} />}
            {transferRequest.classification && <AcuityNeedItem key="acuity" text={transferRequest.classification} />}
          </TableCell>
        )}
        <TableCell className={classes.tableCell}>{getOriginLocationString(transferRequest,true)}</TableCell>
        <TableCell className={classes.tableCell}>{getDestinationLocationString(transferRequest,true)}</TableCell>
        <TableCell className={classes.tableCell}>
          <PriorityBadge priority={transferRequest.priority} />
        </TableCell>
        {scheduleRequestedList.some((r: any) => r.canBeScheduledByCurrentUser && !r.existsPlannerForLocation) && (
          <TableCell className={classes.tableCellStatusWithAction}>
            <div className={classes.requestText}>{getRequestedStatusString(transferRequest)}</div>
            {/* {transferRequest.canBeScheduledByCurrentUser && !transferRequest.existsPlannerForLocation ? (
              <Button style={{ padding: 0, minWidth: "94px" }}>
                <span
                  onClick={(e: any) => {
                    e.stopPropagation();
                    setCurrentTransferId(transferRequest.transferId);
                    setCurrentTransferData(transferRequest);
                    setCurrentBookingId(transferRequest.bookingId);
                    setShowScheduleModal(true);
                  }}
                  className={classes.yellowBoxText}
                >
                  Schedule
                </span>
              </Button>
            ) : null} */}
          </TableCell>
        )}
        {!scheduleRequestedList.some((r: any) => r.canBeScheduledByCurrentUser && !r.existsPlannerForLocation) && (
          <TableCell className={classes.tableCellStatus}>{getRequestedStatusString(transferRequest)}</TableCell>
        )}

        <TableCell style={{ width: 20 }} align="right" className={classes.tableCell}>
          <IconButton aria-label="expand row" size="small">
            <ArrowForwardIosIcon style={{ fontSize: 20 }} />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ height: 1, padding: 0, margin: 0 }}>
          <div style={{ height: 1 }}></div>
        </TableCell>
      </TableRow>
      {isDragging && (
        <DraggingTransfer name={getShortFullName(transferRequest.firstName, transferRequest.lastName)} adjustForTransferRequest={true}
        Draglocation ={null} selectedLocationId={null}/>
      )}
    </>
  );
};

export default TransferRequestItem;
